'use client';

import { useMemo, useState, useEffect, Fragment, MouseEvent } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
// PERFORMANCE: Lazy load lucide-react icons to reduce initial bundle size
// Icons load after initial render to reduce main-thread work
type LucideIcon = React.ComponentType<React.SVGProps<SVGSVGElement>>;
let lucideIconsModule: typeof import('lucide-react') | null = null;
const loadLucideIcons = async () => {
  if (!lucideIconsModule) {
    lucideIconsModule = await import('lucide-react');
  }
  return lucideIconsModule;
};
import { MenuSection, MenuItem } from '@/types';
import { getBrandColor } from '@/config/brand-colors';
import { LazyMarkdownRenderer } from '@/components/ui/LazyMarkdownRenderer';
import S3FilePreview from '@/components/shared/S3FilePreview';
import { cn } from '@/lib/utils';
import { usePreview } from '@/contexts/PreviewContext';
import { useMedia } from '@/contexts/MediaContext';
import { preloadPageMedia } from '@/lib/preload-page-media';
import TikTokIcon from '@/components/icons/TikTokIcon';
import FormRenderer from '@/components/shared/FormRenderer';
import { PublicFormData, PublicPolicyData } from '@/lib/public-site-data';

interface FooterProps {
  menuSection: MenuSection;
  menuItems?: MenuItem[];
  subscriptionForm?: PublicFormData | null;
  policies?: PublicPolicyData[];
}

// Helper function to convert internal URLs to main domain when on any subdomain
const getMenuUrl = (url: string, isExternal?: boolean): string => {
  // Don't modify external links
  if (isExternal) {
    return url;
  }

  // Check if we're on any subdomain (not the main domain)
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    // Check if hostname contains a subdomain (e.g., jobs.e-voque.com, www.e-voque.com, etc.)
    // Main domain would be exactly "e-voque.com" or "www.e-voque.com" (if www is considered main)
    const isSubdomain = hostname !== 'e-voque.com' && hostname.endsWith('.e-voque.com');
    
    if (isSubdomain) {
      // For internal links, convert to main domain
      // Only convert if it's a relative URL (starts with /)
      if (url.startsWith('/')) {
        return `https://e-voque.com${url}`;
      }
    }
  }

  return url;
};

export default function Footer({
  menuSection,
  menuItems = [],
  subscriptionForm = null,
  policies = [],
}: FooterProps) {
  const pathname = usePathname();
  const router = useRouter();
  const preview = usePreview();
  const { prefetchPageMedia } = useMedia();
  // PERFORMANCE: Lazy load lucide-react icons after initial render
  const [iconsLoaded, setIconsLoaded] = useState(false);
  const [Icons, setIcons] = useState<{
    ExternalLink: LucideIcon;
    Instagram: LucideIcon;
    Music: LucideIcon;
  } | null>(null);

  // In preview mode inside the dashboard, derive the effective page path from the editor slug
  const effectivePathname = useMemo(() => {
    if (!pathname) return '/';
    if (preview?.isPreviewMode && pathname.startsWith('/admin/dashboard/pages')) {
      const slugPart = pathname.replace('/admin/dashboard/pages', '').replace(/^\/+/, '');
      return slugPart === '' ? '/' : `/${slugPart}`;
    }
    return pathname;
  }, [pathname, preview?.isPreviewMode]);

  // Navigation handler - optimized for performance with non-blocking preload
  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, url: string, isExternal?: boolean) => {
    // For external links, let the browser handle it
    if (isExternal) {
      return;
    }

    // If we're in preview mode
    if (preview?.isPreviewMode) {
      e.preventDefault();
      e.stopPropagation();
      
      // In edit mode, block all navigation - used for selecting sections
      if (preview.isEditMode) {
        return;
      }
      
      // In navigate mode, navigate within the editor
      const slug = url === '/' ? '' : url.replace(/^\//, ''); // Remove leading slash, empty string for home
      preview.navigateInEditor(slug);
      return;
    }

    // Check if we're already on the target URL
    if (effectivePathname === url) {
      e.preventDefault();
      return;
    }

    // Prevent default to use Next.js Link navigation
    e.preventDefault();
    e.stopPropagation();

    // Validate and normalize URL
    const normalizedUrl = url.trim();
    if (!normalizedUrl || normalizedUrl === '#') {
      console.warn('[Footer] Invalid navigation URL:', normalizedUrl);
      return;
    }

    // Check if URL is absolute (starts with http:// or https://)
    // Next.js router.push() only works with relative URLs, so we need to use window.location for absolute URLs
    const isAbsoluteUrl = normalizedUrl.startsWith('http://') || normalizedUrl.startsWith('https://');
    
    // Non-blocking preload: fire-and-forget to avoid delaying navigation
    // Only preload for relative URLs
    if (!isAbsoluteUrl) {
      const targetSlug = normalizedUrl === '/' ? '' : normalizedUrl.replace(/^\//, '').split('?')[0];
      preloadPageMedia(targetSlug).catch(() => {
        // Silently handle errors - navigation continues regardless
      });
    }

    // Perform navigation with error handling
    try {
      if (isAbsoluteUrl) {
        // For absolute URLs, use window.location for full page navigation
        // This handles cross-domain navigation correctly
        window.location.href = normalizedUrl;
      } else {
        // For relative URLs, use Next.js router for client-side navigation
        // router.push() returns void, not a Promise, so we can't use .catch()
        // If navigation fails, it will be handled by Next.js error boundaries
        router.push(normalizedUrl);
      }
    } catch (error) {
      // Final fallback: use window.location if everything else fails
      console.error('[Footer] Navigation error, using window.location fallback:', error);
      window.location.href = normalizedUrl;
    }
  };

  // Prefetch media and Next.js route on link hover for faster navigation
  const handleLinkHover = (url: string) => {
    if (preview?.isPreviewMode || !url || url.startsWith('http')) return;
    
    const targetSlug = url === '/' ? '' : url.replace(/^\//, '').split('?')[0];
    
    // Prefetch Next.js route (page HTML/JS)
    // This ensures the page is ready when user clicks
    try {
      router.prefetch(url);
    } catch (error) {
      // Silently fail - prefetch is optional
      if (process.env.NODE_ENV === 'development') {
        console.warn('[Footer] Failed to prefetch route:', error);
      }
    }
    
    // Prefetch media for target page
    prefetchPageMedia(targetSlug);
  };

  if (!menuSection || !menuSection.isActive) {
    return null;
  }

  // PERFORMANCE: Lazy load lucide-react icons after initial render
  // Footer is at bottom of page - load these when browser is idle
  // Only run on client-side to avoid SSR issues
  useEffect(() => {
    if (typeof window === 'undefined') return; // Skip during SSR
    if (iconsLoaded) return;
    
    const loadIcons = async () => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(async () => {
          if (!iconsLoaded) {
            const module = await loadLucideIcons();
            setIcons({
              ExternalLink: module.ExternalLink,
              Instagram: module.Instagram,
              Music: module.Music,
            });
            setIconsLoaded(true);
          }
        }, { timeout: 3000 }); // Max wait 3 seconds
      } else {
        // Fallback: load after short delay
        setTimeout(async () => {
          if (!iconsLoaded) {
            const module = await loadLucideIcons();
            setIcons({
              ExternalLink: module.ExternalLink,
              Instagram: module.Instagram,
              Music: module.Music,
            });
            setIconsLoaded(true);
          }
        }, 200);
      }
    };
    
    loadIcons();
  }, [iconsLoaded]);

  // Helper function to check if a menu item is active based on current pathname
  const isMenuItemActive = (itemUrl: string): boolean => {
    if (!effectivePathname) return false;
    
    // Normalize URLs: remove query strings and trailing slashes
    const normalizeUrl = (url: string) => {
      return url.split('?')[0].replace(/\/$/, '') || '/';
    };
    
    const normalizedPathname = normalizeUrl(effectivePathname);
    const normalizedItemUrl = normalizeUrl(itemUrl);
    
    // Exact match
    if (normalizedPathname === normalizedItemUrl) {
      return true;
    }
    
    // Handle home page: both '/' and '' should match
    if ((normalizedPathname === '/' || normalizedPathname === '') && 
        (normalizedItemUrl === '/' || normalizedItemUrl === '')) {
      return true;
    }
    
    return false;
  };

  // Define the desired menu order (main navigation items only)
  const menuOrder: Record<string, number> = {
    'Home': 0,
    'About Us': 1,
    'Services': 2,
    'Job Benefits': 3,
    'Contact Us': 4,
  };
  
  // Function to get sort order for menu items
  const getMenuOrder = (label: string): number => {
    return menuOrder[label] ?? 999; // Items not in the order list go to the end
  };
  
  // Use link groups from JSON
  const jsonLinkGroups = (menuSection.content?.linkGroups as Array<{ title?: string; links: MenuItem[] }>) || [];
  
  // Use JSON link groups
  const linkGroups = jsonLinkGroups.map(group => ({
      title: group.title,
      links: group.links
        .filter(item => item.isActive)
        .sort((a, b) => {
          // First sort by predefined menu order
          const orderA = getMenuOrder(a.label);
          const orderB = getMenuOrder(b.label);
          if (orderA !== orderB) {
            return orderA - orderB;
          }
          // If both are not in the predefined order, use their original order
          return a.order - b.order;
        })
        .map(item => ({
          label: item.label,
          url: item.url,
          isExternal: item.isExternal || false
        }))
    }));

  // Use logo from JSON
  const logo = menuSection.content?.logo as string | undefined;
  const logoAlt = (menuSection.content?.logoAlt as string) || 'Logo';
  // Check if logo exists and is not an empty string
  const hasLogo = logo && logo.trim() !== '';
  const logoUrl = hasLogo && logo ? logo.trim() : undefined;
  
  // Get content from JSON
  const content = menuSection.content?.content as string | undefined;

  // Get social links from JSON
  const socialLinks = (menuSection.content?.socialLinks as Array<{
    platform: string;
    label?: string;
    url: string;
    icon?: string;
    target?: '_self' | '_blank';
    isActive?: boolean;
  }>) || [];

  const renderSocialIcon = (icon?: string, platform?: string) => {
    const iconName = (icon || platform || '').toLowerCase();
    if (iconName === 'instagram') {
      return Icons?.Instagram ? <Icons.Instagram className="w-5 h-5" /> : <span className="w-5 h-5" />;
    }
    if (iconName === 'tiktok') return <TikTokIcon className="w-5 h-5" size={20} />;
    if (iconName === 'music' || iconName === 'spotify') {
      return Icons?.Music ? <Icons.Music className="w-5 h-5" /> : <span className="w-5 h-5" />;
    }
    return Icons?.ExternalLink ? <Icons.ExternalLink className="w-5 h-5" /> : <span className="w-5 h-5" />;
  };

  // Create subtle background pattern
  const backgroundPattern = useMemo(() => {
    const navy = getBrandColor('deepNavy');
    const blue = getBrandColor('primaryBlue');
    const orange = getBrandColor('accentOrange');
    
    const svg = `<svg width="120" height="120" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <pattern id="footer-pattern" x="0" y="0" width="120" height="120" patternUnits="userSpaceOnUse">
          <rect width="120" height="120" fill="#ffffff"/>
          <circle cx="10" cy="10" r="1.5" fill="${navy}" opacity="0.08"/>
          <circle cx="60" cy="30" r="1" fill="${blue}" opacity="0.06"/>
          <circle cx="110" cy="50" r="1.5" fill="${orange}" opacity="0.05"/>
          <circle cx="30" cy="90" r="1" fill="${blue}" opacity="0.06"/>
          <circle cx="90" cy="110" r="1.5" fill="${navy}" opacity="0.08"/>
          <line x1="0" y1="60" x2="120" y2="60" stroke="${navy}" stroke-width="0.5" opacity="0.03"/>
          <line x1="60" y1="0" x2="60" y2="120" stroke="${blue}" stroke-width="0.5" opacity="0.03"/>
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#footer-pattern)"/>
    </svg>`;
    
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
  }, []);

  return (
    <footer 
      className="w-full mt-auto relative overflow-hidden !bg-white" 
      style={{ 
        backgroundColor: '#ffffff',
        color: getBrandColor('black'),
        backgroundImage: `url("${backgroundPattern}")`,
        backgroundSize: '120px 120px',
        backgroundPosition: '0 0',
        backgroundBlendMode: 'normal',
        backgroundRepeat: 'repeat',
      }}
    >
      {/* Explicit white background layer to ensure white background on mobile */}
      <div 
        className="absolute inset-0 bg-white z-0"
        style={{ backgroundColor: '#ffffff' }}
      />
      
      {/* Decorative top border gradient */}
      <div 
        className="absolute top-0 left-0 right-0 h-px z-10"
        style={{
          background: `linear-gradient(90deg, transparent 0%, ${getBrandColor('primaryBlue')}40 50%, transparent 100%)`
        }}
      />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 lg:py-20 w-full relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col gap-10 md:gap-14">
            {/* Logo, Links, and Subscription Row */}
            <div className="flex flex-col lg:flex-row gap-10 md:gap-12 lg:gap-16 items-start">
              {/* Logo Section */}
              {hasLogo && (
                <div className="flex-shrink-0 flex items-center justify-center md:justify-start">
                  <Link
                    href={getMenuUrl('/', false)} 
                    prefetch={true}
                    className="flex items-center transition-all duration-300 hover:scale-105 hover:opacity-90 group" 
                    onClick={(e) => handleLinkClick(e, getMenuUrl('/', false), false)}
                    onMouseEnter={() => handleLinkHover('/')}
                  >
                    <div 
                      className="relative h-16 md:h-20 lg:h-24 transition-all duration-300 group-hover:drop-shadow-lg" 
                      style={{ 
                        width: '200px', 
                        maxWidth: '240px',
                        filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.08))'
                      }}
                    >
                      <S3FilePreview
                        src={logoUrl || ''}
                        alt={logoAlt}
                        className="w-full h-full object-contain"
                        priority={true}
                        disablePreviewModal={true}
                        disableSkeleton={false}
                      />
                    </div>
                  </Link>
                </div>
              )}

              {/* Link Groups - Takes remaining width */}
              {linkGroups.length > 0 && (
                <div className="flex-1 w-full">
                  <nav
                    className={cn(
                      'grid gap-y-6 gap-x-10 md:gap-x-12',
                      linkGroups.length === 1 && 'grid-cols-1',
                      linkGroups.length === 2 && 'grid-cols-1 sm:grid-cols-2',
                      linkGroups.length >= 3 && 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
                    )}
                  >
                    {linkGroups.map((group, groupIndex) => (
                      <div key={groupIndex} className="relative flex flex-col gap-3">
                        {group.title && (
                          <h3
                            className="font-bold text-lg md:text-xl mb-2 relative inline-block" 
                            style={{ color: getBrandColor('deepNavy') }}
                          >
                            {group.title}
                            <span 
                              className="absolute bottom-0 left-0 w-12 h-0.5 rounded-full"
                              style={{ 
                                backgroundColor: getBrandColor('accentOrange'),
                                transform: 'translateY(4px)'
                              }}
                            />
                          </h3>
                        )}
                        <div className="flex flex-col gap-2.5">
                          {group.links.map((link, linkIndex) => {
                            const isActive = isMenuItemActive(link.url);
                            return (
                              <div key={`${groupIndex}-${linkIndex}`}>
                                <Link
                                  href={getMenuUrl(link.url, link.isExternal)}
                                  prefetch={!link.isExternal}
                                  target={link.isExternal ? '_blank' : undefined}
                                  rel={link.isExternal ? 'noopener noreferrer' : undefined}
                                  onClick={(e) => handleLinkClick(e, getMenuUrl(link.url, link.isExternal), link.isExternal)}
                                  className={cn(
                                    'group relative flex items-center gap-2 transition-all duration-300 text-sm md:text-base px-4 py-2.5 rounded-lg',
                                    !isActive && 'hover:translate-x-2 hover:shadow-sm'
                                  )}
                                  style={{ 
                                    color: isActive ? getBrandColor('white') : getBrandColor('black') + 'E6',
                                    backgroundColor: isActive 
                                      ? `linear-gradient(135deg, ${getBrandColor('primaryBlue')}, ${getBrandColor('deepNavy')})` 
                                      : 'transparent',
                                    background: isActive 
                                      ? `linear-gradient(135deg, ${getBrandColor('primaryBlue')}, ${getBrandColor('deepNavy')})` 
                                      : 'transparent',
                                    boxShadow: isActive 
                                      ? `0 4px 12px ${getBrandColor('primaryBlue')}40` 
                                      : 'none',
                                  }}
                                  onMouseEnter={(e: MouseEvent<HTMLAnchorElement>) => {
                                    if (!link.isExternal) {
                                      handleLinkHover(link.url);
                                    }
                                    if (!isActive) {
                                      e.currentTarget.style.color = getBrandColor('accentOrange');
                                      e.currentTarget.style.backgroundColor = getBrandColor('primaryBlue') + '08';
                                    }
                                  }}
                                  onMouseLeave={(e: MouseEvent<HTMLAnchorElement>) => {
                                    if (!isActive) {
                                      e.currentTarget.style.color = getBrandColor('black') + 'E6';
                                      e.currentTarget.style.backgroundColor = 'transparent';
                                    }
                                  }}
                                >
                                  <span className="relative z-10 flex items-center gap-2">
                                    {link.label}
                                    {link.isExternal && (
                                      Icons?.ExternalLink ? (
                                        <Icons.ExternalLink 
                                          className="w-3.5 h-3.5 opacity-60 group-hover:opacity-100 transition-opacity" 
                                          style={{ color: 'currentColor' }}
                                        />
                                      ) : (
                                        <span className="w-3.5 h-3.5 opacity-60 group-hover:opacity-100 transition-opacity" />
                                      )
                                    )}
                                  </span>
                                  {!isActive && (
                                    <span 
                                      className="absolute left-0 top-0 bottom-0 w-1 rounded-r-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                                      style={{ backgroundColor: getBrandColor('accentOrange') }}
                                    />
                                  )}
                                </Link>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </nav>
                </div>
              )}

              {/* Subscription Form - Fourth Column */}
              {subscriptionForm && (
                <div className="w-full lg:w-auto lg:min-w-[280px] lg:max-w-[320px]">
                  <h3
                    className="font-bold text-lg md:text-xl mb-6 relative inline-block" 
                    style={{ color: getBrandColor('deepNavy') }}
                  >
                    Subscribe to our Newsletter
                    <span 
                      className="absolute bottom-0 left-0 w-12 h-0.5 rounded-full"
                      style={{ 
                        backgroundColor: getBrandColor('accentOrange'),
                        transform: 'translateY(4px)'
                      }}
                    />
                  </h3>
                  <div className="w-full">
                    <FormRenderer 
                      form={{
                        id: subscriptionForm.id,
                        name: subscriptionForm.name,
                        slug: subscriptionForm.slug,
                        description: subscriptionForm.description || null,
                        fields: subscriptionForm.fields.map(f => ({
                          id: f.id,
                          name: f.name,
                          label: f.label,
                          type: f.type,
                          placeholder: f.placeholder,
                          required: f.required,
                          options: f.options,
                          validation: f.validation,
                          order: f.order,
                        })),
                        settings: (subscriptionForm as any).settings || undefined,
                      }}
                      className="space-y-3"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Content */}
            {content && (
              <div
                key={content}
                className="prose prose-sm max-w-none relative"
                style={{
                  color: getBrandColor('black') + 'E6'
                }}
              >
                <div 
                  className="absolute -left-4 top-0 bottom-0 w-1 rounded-full opacity-30"
                  style={{ backgroundColor: getBrandColor('accentElectricBlue') }}
                />
                <div className="pl-6">
                  <LazyMarkdownRenderer content={content} />
                </div>
              </div>
            )}

            {/* Bottom Copyright/Divider */}
            <div className="pt-8 md:pt-12 relative">
              {/* Decorative divider with gradient */}
              <div 
                className="absolute top-0 left-0 right-0 h-px mb-8"
                style={{
                  background: `linear-gradient(90deg, transparent 0%, ${getBrandColor('deepNavy')}30 20%, ${getBrandColor('primaryBlue')}40 50%, ${getBrandColor('deepNavy')}30 80%, transparent 100%)`
                }}
              />
              <div className="flex flex-col gap-6 mt-8">
                {/* Social Links */}
                {socialLinks.length > 0 && (
                  <div className="flex items-center gap-4 justify-center">
                    {socialLinks
                      .filter(link => link.isActive !== false)
                      .map((link, index) => {
                        const label = link.label || link.platform || 'Social link';
                        const target = link.target || '_blank';

                        return (
                          <a
                            key={index}
                            href={link.url}
                            target={target}
                            rel={target === '_blank' ? 'noopener noreferrer' : undefined}
                            className="flex items-center justify-center w-10 h-10 rounded-full transition-all duration-300"
                            style={{
                              backgroundColor: getBrandColor('primaryBlue') + '15',
                              color: getBrandColor('primaryBlue'),
                              border: `1px solid ${getBrandColor('primaryBlue')}30`,
                            }}
                            onMouseEnter={(e: MouseEvent<HTMLAnchorElement>) => {
                              e.currentTarget.style.backgroundColor = getBrandColor('primaryBlue');
                              e.currentTarget.style.color = getBrandColor('white');
                              e.currentTarget.style.borderColor = getBrandColor('primaryBlue');
                            }}
                            onMouseLeave={(e: MouseEvent<HTMLAnchorElement>) => {
                              e.currentTarget.style.backgroundColor = getBrandColor('primaryBlue') + '15';
                              e.currentTarget.style.color = getBrandColor('primaryBlue');
                              e.currentTarget.style.borderColor = getBrandColor('primaryBlue') + '30';
                            }}
                            aria-label={label}
                            title={label}
                          >
                            {renderSocialIcon(link.icon, link.platform)}
                            <span className="sr-only">{label}</span>
                          </a>
                        );
                      })}
                  </div>
                )}

                {/* Policy Links */}
                {policies.length > 0 && (
                  <div className="flex flex-wrap items-center justify-center gap-4 text-sm">
                    {policies.map((policy, index) => (
                      <Fragment key={policy.id}>
                        {index > 0 && (
                          <span 
                            className="hidden sm:inline w-1 h-1 rounded-full"
                            style={{ backgroundColor: getBrandColor('deepNavy') + '40' }}
                          />
                        )}
                        <Link
                          href={`/policies/${policy.slug}`}
                          prefetch={true}
                          onClick={(e) => handleLinkClick(e, `/policies/${policy.slug}`, false)}
                          onMouseEnter={(e: MouseEvent<HTMLAnchorElement>) => {
                            e.currentTarget.style.color = getBrandColor('primaryBlue');
                            handleLinkHover(`/policies/${policy.slug}`);
                          }}
                          onMouseLeave={(e: MouseEvent<HTMLAnchorElement>) => {
                            e.currentTarget.style.color = getBrandColor('deepNavy') + 'B3';
                          }}
                          className="transition-colors duration-200 hover:underline underline-offset-4"
                          style={{ 
                            color: getBrandColor('deepNavy') + 'B3',
                          }}
                        >
                          {policy.displayTitle}
                        </Link>
                      </Fragment>
                    ))}
                  </div>
                )}

                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  <p 
                    className="text-center md:text-left text-sm md:text-base font-medium"
                    style={{ color: getBrandColor('black') + 'B3' }}
                  >
                    © {new Date().getFullYear()} e-voque. All rights reserved.
                  </p>
                  <div 
                    className="flex items-center gap-2 text-xs justify-center"
                    style={{ color: getBrandColor('deepNavy') + 'CC' }}
                  >
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: getBrandColor('accentOrange') }} />
                    <Link href="https://dishub.city" target="_blank" rel="noopener noreferrer" style={{ color: getBrandColor('deepNavy') + 'CC' }}>
                      Built with Frond CMS by Dishub
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
